import { appKey } from '@app/shared/brand'
import { useQueries, useQuery } from '@tanstack/react-query'
import { Link, useSearch } from '@tanstack/react-router'
import {
  Folders,
  History,
  Inbox,
  LayoutGrid,
  type LucideIcon,
  Plus,
} from 'lucide-react'
import { type CSSProperties, type ReactNode, useMemo, useState } from 'react'
import { useSpace } from '../../lib/org'
import { useTRPC } from '../../lib/trpc'
import { Button, Caret, Tooltip } from '../../ui'
import { isCover } from './DocCover'
import { FolderName } from './FolderName'
import type { FolderTree, Movable, TreeFolder } from './folder-tree'
import {
  DIRECTION_ICON,
  folderIcon,
  sharedOutFolders,
  useSharedWithMe,
} from './sharing'
import { type ShelfSpace, useShelf } from './shelf'
import { useDropTarget } from './useDocDrag'
import { useFolderMoves, useFolderTree } from './useFolderMoves'
import './folder-nav.css'

type Move = (item: Movable, target: string | null) => void

const FOLDER_PATH = /^\/documents\/folder\/([^/]+)/

/**
 * The space the address names other than the active one, or null. While one
 * is named the page is that space's, so none of the active space's own
 * entries is where the person is.
 */
function useOtherSpace(): string | null {
  const { spaceId } = useSpace()
  /* Loose: the sidebar sits over every route, and only the documents
     routes declare `space`. */
  const search = useSearch({ strict: false }) as Record<string, unknown>
  const named = typeof search.space === 'string' ? search.space : ''
  return named && named !== spaceId ? named : null
}
const SHARED_FOLDER_PATH = /^\/documents\/shared-folder\/([^/]+)/

/**
 * Which folders of a tree are open, and the one on screen with every folder
 * above it opened, so where it sits is in view.
 */
function useOpenBranches(tree: FolderTree, current: string | null) {
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set())
  const [shownFor, setShownFor] = useState<string | null>(null)
  /* Answered once the folder is in the tree, which the first render may
     precede. */
  const known = current && tree.byId.has(current) ? current : null
  if (known !== shownFor) {
    setShownFor(known)
    if (known) {
      const path = tree.pathOf(known).slice(0, -1)
      if (path.some((f) => !open.has(f.id)))
        setOpen(new Set([...open, ...path.map((f) => f.id)]))
    }
  }
  const toggle = (id: string, to?: boolean) =>
    setOpen((was) => {
      const next = new Set(was)
      if (to ?? !was.has(id)) next.add(id)
      else next.delete(id)
      return next
    })
  return { open, toggle }
}

/**
 * The space's own items, read once and shared by every count here. A book's
 * cover is counted as the book, as the list shows it, not again as a page.
 */
function useOwnItems() {
  const trpc = useTRPC()
  const { spaceId } = useSpace()
  return useQuery({
    ...trpc.documents.list.queryOptions({ spaceId: spaceId ?? undefined }),
    enabled: !!spaceId,
    select: (docs) => docs.filter((w) => !isCover(w)),
  }).data
}

/**
 * The ways into everything a person has made: all of it, what waits in
 * Unfiled, and the folders at the top. The last two take a dropped item, so a
 * card picked up on the page is filed — or unfiled — by putting it down here.
 */
export function DocNav({
  pathname,
  onPick,
}: {
  pathname: string
  onPick?: () => void
}) {
  const { spaceId, editsDocuments } = useSpace()
  const { tree, move, dialog } = useFolderMoves(spaceId)
  const items = useOwnItems()
  const everything = useShelfCount()
  const { alone } = useSharedWithMe()
  const own = useOtherSpace() === null
  const onAll =
    own &&
    (pathname === '/documents' ||
      pathname === '/documents/recent' ||
      pathname === '/documents/shared' ||
      pathname === '/documents/deleted')
  return (
    <>
      {dialog}
      <NavEntry
        to="/documents"
        label="All documents"
        icon={LayoutGrid}
        current={onAll}
        count={everything}
        onPick={onPick}
      />
      {/* What is shared with the person comes before what they file, and
          takes no drop: somebody else's things are not filed here. */}
      <NavEntry
        to="/documents/shared-with-me"
        label="Shared with me"
        icon={DIRECTION_ICON.in}
        current={pathname === '/documents/shared-with-me'}
        count={alone.length}
        onPick={onPick}
      />
      <NavEntry
        to="/documents/unfiled"
        label="Unfiled"
        icon={Inbox}
        current={own && pathname === '/documents/unfiled'}
        count={items?.filter((w) => w.folderId === null).length}
        drop={{ target: null, accepts: 'doc', tree, move }}
        onPick={onPick}
      />
      <NavEntry
        to="/documents/folders"
        label="All folders"
        icon={Folders}
        current={own && pathname === '/documents/folders'}
        drop={{ target: null, accepts: 'folder', tree, move }}
        onPick={onPick}
      />
      {/* The space's trail of shares, links and moves is its holder's, as its
          bin is, so it is offered only where the person holds the space. */}
      {editsDocuments ? (
        <NavEntry
          to="/documents/activity"
          label="Activity"
          icon={History}
          current={own && pathname === '/documents/activity'}
          onPick={onPick}
        />
      ) : null}
    </>
  )
}

/**
 * How many of the shelf's own documents there are, across every space of it, a
 * book's cover counted as the book. What is shared with the person is counted
 * under Shared with me, not here, though All documents lists it too. Undefined
 * until every space has answered, so the count never shows a part as the whole.
 */
function useShelfCount(): number | undefined {
  const trpc = useTRPC()
  const shelf = useShelf()
  const lists = useQueries({
    queries: shelf.map((s) =>
      trpc.documents.list.queryOptions({ spaceId: s.spaceId }),
    ),
  })
  if (shelf.length === 0 || lists.some((q) => !q.data)) return undefined
  return lists.reduce(
    (n, q) => n + (q.data ?? []).filter((w) => !isCover(w)).length,
    0,
  )
}

type EntryProps = {
  to:
    | '/documents'
    | '/documents/shared-with-me'
    | '/documents/unfiled'
    | '/documents/folders'
    | '/documents/activity'
  /** The space the entry is of, where it is not the one Studio is in. */
  space?: string
  label: string
  icon: LucideIcon
  current: boolean
  count?: number
  onPick?: () => void
}

/** One top-level entry, and somewhere to drop where it says so. */
function NavEntry({
  drop,
  ...entry
}: EntryProps & { drop?: Parameters<typeof useDropTarget>[0] }) {
  return drop ? <DropEntry {...entry} drop={drop} /> : <EntryLink {...entry} />
}

function DropEntry({
  drop,
  ...entry
}: EntryProps & { drop: Parameters<typeof useDropTarget>[0] }) {
  const target = useDropTarget(drop)
  return (
    <EntryLink {...entry} isOver={target.isOver} dropProps={target.props} />
  )
}

function EntryLink({
  to,
  space,
  label,
  icon: Icon,
  current,
  count,
  onPick,
  isOver = false,
  dropProps,
}: EntryProps & {
  isOver?: boolean
  dropProps?: ReturnType<typeof useDropTarget>['props']
}) {
  return (
    <Link
      to={to}
      search={space ? { space } : {}}
      className="app-sidebar__item folder-nav__drop"
      /* The entry states its own mark; the router's would light All documents on
         every address beneath it. */
      activeOptions={{ exact: true }}
      aria-current={current ? 'page' : undefined}
      data-drop={isOver ? '' : undefined}
      onClick={onPick}
      {...dropProps}
    >
      <Icon size={16} aria-hidden />
      <span className="app-sidebar__label">{label}</span>
      {count !== undefined ? <Count value={count} /> : null}
    </Link>
  )
}

function Count({ value }: { value: number }) {
  return <span className="folder-nav__count">{value}</span>
}

/**
 * The folders, as a tree under their own heading, each a place to go and a
 * place to drop. Absent on the icon rail, where All folders reaches them.
 */
export function FolderTreeNav({
  pathname,
  collapsed,
  onPick,
}: {
  pathname: string
  collapsed: boolean
  onPick?: () => void
}) {
  const { spaceId, editsDocuments } = useSpace()
  const { tree, move, dialog } = useFolderMoves(spaceId)
  const items = useOwnItems()
  const current = pathname.match(FOLDER_PATH)?.[1] ?? null
  /* A folder of another space on screen is not one to make a folder in. */
  const here = current && tree.byId.has(current) ? current : null
  const [making, setMaking] = useState(false)
  const { open, toggle } = useOpenBranches(tree, current)
  const outFolders = useMemo(() => sharedOutFolders(tree), [tree])
  const countIn = (id: string) => {
    const inside = tree.within(id)
    return (items ?? []).filter(
      (w) => w.folderId !== null && inside.has(w.folderId),
    ).length
  }

  const orgs = useShelf().filter((s) => !s.personal && s.spaceId !== spaceId)

  if (collapsed || !spaceId) return null
  /* A reader makes no folders, so a space with none has nothing to show here. */
  const empty = !editsDocuments && tree.childrenOf(null).length === 0
  return (
    <>
      <SharedFoldersNav pathname={pathname} onPick={onPick} />
      {empty ? null : (
        <section className="folder-nav" aria-labelledby="folder-nav-head">
          {dialog}
          <div className="folder-nav__head">
            <h2 id="folder-nav-head" className="folder-nav__title">
              Folders
            </h2>
            {editsDocuments ? (
              <Tooltip label="New folder">
                <Button
                  variant="quiet"
                  icon
                  aria-label="New folder"
                  onClick={() => setMaking(true)}
                >
                  <Plus size={14} aria-hidden />
                </Button>
              </Tooltip>
            ) : null}
          </div>
          {tree.childrenOf(null).length > 0 ? (
            <div className="folder-nav__list">
              {tree.childrenOf(null).map((f) => (
                <FolderBranch
                  key={f.id}
                  folder={f}
                  top
                  depth={0}
                  tree={tree}
                  place="own"
                  drop={move}
                  iconFor={(id) =>
                    folderIcon(outFolders.has(id) ? 'out' : null)
                  }
                  open={open}
                  toggle={toggle}
                  current={current}
                  countIn={countIn}
                  onPick={onPick}
                />
              ))}
            </div>
          ) : null}
          {making ? (
            <FolderName
              spaceId={spaceId}
              /* Made beside where the person is: inside the folder on screen. */
              parentId={here}
              onClose={() => setMaking(false)}
            />
          ) : null}
        </section>
      )}
      {orgs.map((s) => (
        <SpaceFoldersNav
          key={s.spaceId}
          space={s}
          pathname={pathname}
          onPick={onPick}
        />
      ))}
    </>
  )
}

/**
 * Whether an org's group in the nav is folded, kept per browser for each org.
 * Until somebody folds or opens it, an org is shut unless its documents are
 * the ones on screen: a person in several orgs reads one at a time.
 */
function useSpaceFold(
  spaceId: string,
  shutUnlessChosen: boolean,
): [boolean, (shut: boolean) => void] {
  const key = appKey(`studio-space-fold:${spaceId}`)
  const [chosen, setChosen] = useState<boolean | null>(() => {
    try {
      const stored = localStorage.getItem(key)
      return stored === 'shut' ? true : stored === 'open' ? false : null
    } catch {
      return null
    }
  })
  const choose = (shut: boolean) => {
    setChosen(shut)
    try {
      localStorage.setItem(key, shut ? 'shut' : 'open')
    } catch {
      /* Held for this visit only. */
    }
  }
  return [chosen ?? shutUnlessChosen, choose]
}

/**
 * One org's folders, in a person's own space: the org's name heading them and
 * reaching its documents, each folder opening in the org's space. Nothing is
 * dropped here — a document crosses into another space only as a move of its
 * own, confirmed — and an org with no folders shows its name alone.
 */
function SpaceFoldersNav({
  space,
  pathname,
  onPick,
}: {
  space: ShelfSpace
  pathname: string
  onPick?: () => void
}) {
  const trpc = useTRPC()
  const { tree } = useFolderTree(space.spaceId)
  const items = useQuery({
    ...trpc.documents.list.queryOptions({ spaceId: space.spaceId }),
    select: (docs) => docs.filter((w) => !isCover(w)),
  }).data
  const current = pathname.match(FOLDER_PATH)?.[1] ?? null
  const { open, toggle } = useOpenBranches(tree, current)
  const countIn = (id: string) => {
    const inside = tree.within(id)
    return (items ?? []).filter(
      (w) => w.folderId !== null && inside.has(w.folderId),
    ).length
  }
  const head = `space-nav-${space.spaceId}`
  const named = useOtherSpace()
  const onShelf = pathname === '/documents' && named === space.spaceId
  const [shut, setShut] = useSpaceFold(space.spaceId, named !== space.spaceId)
  return (
    <section className="folder-nav" aria-labelledby={head}>
      <div className="folder-nav__head">
        <h2 id={head} className="folder-nav__title">
          <Link
            to="/documents"
            search={{ space: space.spaceId }}
            className="folder-nav__space"
            /* Its own documents only, not every address carrying its name. */
            activeOptions={{ exact: true, includeSearch: true }}
            aria-current={onShelf ? 'page' : undefined}
            onClick={onPick}
          >
            {space.name}
          </Link>
        </h2>
        <Caret
          expanded={!shut}
          label={`${shut ? 'Show' : 'Hide'} ${space.name}`}
          onToggle={() => setShut(!shut)}
        />
      </div>
      {/* Its trail is offered to whoever holds it, as its bin is. */}
      {!shut && space.edits ? (
        <EntryLink
          to="/documents/activity"
          space={space.spaceId}
          label="Activity"
          icon={History}
          current={
            pathname === '/documents/activity' && named === space.spaceId
          }
          onPick={onPick}
        />
      ) : null}
      {!shut && tree.childrenOf(null).length > 0 ? (
        <div className="folder-nav__list">
          {tree.childrenOf(null).map((f) => (
            <FolderBranch
              key={f.id}
              folder={f}
              top
              depth={0}
              tree={tree}
              place="own"
              space={space.spaceId}
              iconFor={() => folderIcon(null)}
              open={open}
              toggle={toggle}
              current={current}
              countIn={countIn}
              onPick={onPick}
            />
          ))}
        </div>
      ) : null}
    </section>
  )
}

/**
 * The folders other people share with this person, each one heading its own
 * branch, above the person's own. Read-only for structure: nothing is dropped
 * here, since what is filed in somebody else's folder is theirs to arrange.
 * Absent while nothing is shared.
 */
function SharedFoldersNav({
  pathname,
  onPick,
}: {
  pathname: string
  onPick?: () => void
}) {
  const { tree, docs } = useSharedWithMe()
  const current = pathname.match(SHARED_FOLDER_PATH)?.[1] ?? null
  const { open, toggle } = useOpenBranches(tree, current)
  const countIn = (id: string) => {
    const inside = tree.within(id)
    return (docs.data ?? []).filter(
      (d) => d.folderId !== null && inside.has(d.folderId),
    ).length
  }
  const tops = tree.childrenOf(null)
  if (tops.length === 0) return null
  return (
    <section className="folder-nav" aria-labelledby="shared-folder-nav-head">
      <div className="folder-nav__head">
        <h2 id="shared-folder-nav-head" className="folder-nav__title">
          Shared folders
        </h2>
      </div>
      <div className="folder-nav__list">
        {tops.map((f) => (
          <FolderBranch
            key={f.id}
            folder={f}
            top
            depth={0}
            tree={tree}
            place="shared"
            iconFor={() => folderIcon('in')}
            open={open}
            toggle={toggle}
            current={current}
            countIn={countIn}
            onPick={onPick}
          />
        ))}
      </div>
    </section>
  )
}

type BranchProps = {
  folder: TreeFolder
  /**
   * A folder at the top of the tree is an entry like the sidebar's own, with
   * its icon; what it holds hangs beneath it on the rail, as a section's pages
   * do.
   */
  top?: boolean
  depth: number
  tree: FolderTree
  /**
   * Where the folder opens: among a space's own folders, or as a folder
   * somebody shares with the person, browsed as they arrange it.
   */
  place: 'own' | 'shared'
  /** The space it is in, where that is not the one Studio is in. */
  space?: string
  /** Where a dropped item is filed: the person's own folders take one. */
  drop?: Move
  iconFor: (id: string) => LucideIcon
  open: ReadonlySet<string>
  toggle: (id: string, to?: boolean) => void
  current: string | null
  countIn: (id: string) => number
  onPick?: () => void
}

function FolderBranch(props: BranchProps) {
  return props.drop ? (
    <DropBranch {...props} drop={props.drop} />
  ) : (
    <Branch {...props} />
  )
}

function DropBranch(props: BranchProps & { drop: Move }) {
  const { folder, tree, drop, toggle } = props
  const target = useDropTarget({
    target: folder.id,
    tree,
    move: drop,
    onSpringOpen:
      tree.childrenOf(folder.id).length > 0
        ? () => toggle(folder.id, true)
        : undefined,
  })
  return <Branch {...props} target={target} />
}

function Branch({
  target,
  ...props
}: BranchProps & { target?: ReturnType<typeof useDropTarget> }) {
  const {
    folder,
    top = false,
    depth,
    tree,
    place,
    space,
    iconFor,
    open,
    toggle,
    current,
    countIn,
    onPick,
  } = props
  const children = tree.childrenOf(folder.id)
  const expanded = open.has(folder.id)
  const held = countIn(folder.id)
  const Icon = iconFor(folder.id)
  const fold: ReactNode =
    children.length > 0 ? (
      <Caret
        expanded={expanded}
        label={`${expanded ? 'Hide' : 'Show'} the folders in ${folder.name}`}
        onToggle={() => toggle(folder.id)}
      />
    ) : null
  const link = (
    <>
      {top ? <Icon size={16} aria-hidden /> : null}
      <span className="folder-nav__name">{folder.name}</span>
    </>
  )
  return (
    <>
      <div
        className={`${top ? 'app-sidebar__item folder-nav__row--top' : 'app-sidebar__page'} folder-nav__row${target ? ' folder-nav__drop' : ''}`}
        data-drop={target?.isOver ? '' : undefined}
        data-current={current === folder.id ? '' : undefined}
        style={{ '--folder-depth': depth } as CSSProperties}
        {...target?.props}
      >
        {place === 'own' ? (
          <Link
            to="/documents/folder/$id"
            params={{ id: folder.id }}
            search={space ? { space } : {}}
            className="folder-nav__link"
            activeOptions={{ exact: true }}
            aria-current={current === folder.id ? 'page' : undefined}
            onClick={onPick}
          >
            {link}
          </Link>
        ) : (
          <Link
            to="/documents/shared-folder/$id"
            params={{ id: folder.id }}
            className="folder-nav__link"
            activeOptions={{ exact: true }}
            aria-current={current === folder.id ? 'page' : undefined}
            onClick={onPick}
          >
            {link}
          </Link>
        )}
        {fold ? <span className="folder-nav__fold">{fold}</span> : null}
        {/* An empty folder has nothing to count, so it says nothing. */}
        {held > 0 ? <Count value={held} /> : null}
      </div>
      {expanded && children.length > 0
        ? (() => {
            const branches = children.map((c) => (
              <FolderBranch
                key={c.id}
                {...props}
                folder={c}
                top={false}
                depth={top ? 0 : depth + 1}
              />
            ))
            return top ? (
              <div className="app-sidebar__pages folder-nav__rail">
                {branches}
              </div>
            ) : (
              branches
            )
          })()
        : null}
    </>
  )
}
